import { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import Loaders from "@stadium/ui/src/components/loaders/loaders";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

import ConfirmationBanner from "@components/store/confirmation/banner";
import OrderConfirmationExtraInfo from "@components/store/confirmation/extra-info";
import { useStoreContext } from "@components/store/provider";
import CancelOrder from "@components/store/confirmation/cancel-order";

import useFetchOrder from "@hooks/order/use-fetch-details";

import { useOpenOrderContext } from "@contexts/open-order-context";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";
import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";

import { isOrderCompleted } from "@helpers/orders/cart";

import { getIsKitStore, getIsPricelessShop } from "@selectors/store";

import { getStoreHomeRoute } from "@constants/routes";
import { DASHBOARD_URL } from "@stadium/helpers/app-routes";

import styles from "./main.module.scss";

const Confirmation = ({ orderNumber }) => {
  const router = useRouter();
  const { fetchOrder, order, loading } = useFetchOrder();
  const { resetOpenOrder } = useOpenOrderContext();
  const { storeBasePath } = useStoreContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const isKitStore = useSelector(getIsKitStore);
  const isPriceLessShop = useSelector(getIsPricelessShop);

  const { location, services, baseEventLocationProperties } = useEventLocationContext();
  const { trackPage } = useActionEvents(services);

  useEffect(() => {
    if (order && !isOrderCompleted(order)) {
      if (isKitStore) {
        window.location.href = DASHBOARD_URL;
        return;
      }
      const link = getStoreHomeRoute({ storeBasePath });
      router.push(link);
    }
  }, [router, order, storeBasePath, isKitStore]);

  useEffect(() => {
    fetchOrder(orderNumber);
    resetOpenOrder();
  }, [fetchOrder, orderNumber]);

  useEffect(() => {
    if (loading || isEmpty(order)) return;
    if (order.number != null && String(order.number) !== String(orderNumber)) return;

    trackPage({
      page: location,
      order_number: orderNumber,
      cart_value: +(order.bagTotal || 0),
      item_count: order.itemCount,
      ...baseEventLocationProperties,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    location,
    orderNumber,
    trackPage,
    order?.number,
    order?.bagTotal,
    order?.itemCount,
    baseEventLocationProperties,
  ]);

  const isOrderVerified = useMemo(() => order?.isVerified, [order]);
  const isReOrderableOrder = isKitStore || !isOrderVerified || isPriceLessShop;

  if (loading) return <Loaders.PageLoader isLoading wrapperClass="pageLoaderwrap" />;

  if (isEmpty(order)) return null;

  if (order?.state === "canceled")
    return <CancelOrder orderNumber={orderNumber} isReOrderableOrder={isReOrderableOrder} />;
  return (
    <BottomIconHideWrapper hideIcon={isMobileDevice}>
      <div className={styles.orderConfirmContaiunerFluid}>
        <div className={styles.orderConfirmationWrapper}>
          <ConfirmationBanner
            orderCountryId={order?.countryId}
            orderNumber={orderNumber}
            orderAddressId={order?.shipAddressStadiumId}
            orderCompletedAt={order?.completedAt}
            isVerified={isOrderVerified}
            isReOrderableOrder={isReOrderableOrder}
            fetchOrder={fetchOrder}
            editableAfterCheckout={order?.editableAfterCheckout}
          />
          {order && <OrderConfirmationExtraInfo order={order} orderNumber={orderNumber} />}
        </div>
      </div>
    </BottomIconHideWrapper>
  );
};

export default Confirmation;
